import type {
  TeamAiSkillCategory,
  TeamAiSkillCreateInput,
  TeamAiSkillScope,
  TeamAiSkillVersionState,
} from './admin-team-ai-skills.types';

export const MAX_SKILL_CONTENT_LENGTH = 16_000;
export const MAX_IMPORT_FILE_BYTES = 64 * 1024;
export const CATEGORY_OPTIONS: TeamAiSkillCategory[] = [
  'behavior',
  'runtime',
  'domain',
  'template',
];
export const SCOPE_OPTIONS: TeamAiSkillScope[] = [
  'platform',
  'runtime',
  'template',
  'tenant',
  'project',
];
export const EMPTY_SKILL_FORM: TeamAiSkillCreateInput = {
  key: '',
  name: '',
  description: '',
  agent: 'pm',
  category: 'behavior',
  content: '',
  executable: null,
};

const SKILL_IMPORT_FIELDS = [
  'agent',
  'category',
  'content',
  'description',
  'executable',
  'key',
  'name',
] as const;

export function formatSkillDate(value: string | null): string {
  if (!value) return 'Not available';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function skillStateBadgeVariant(
  state: TeamAiSkillVersionState,
): 'secondary' | 'success' | 'warning' | 'stopped' {
  if (state === 'published') return 'success';
  if (state === 'evaluated') return 'warning';
  if (state === 'retired') return 'stopped';
  return 'secondary';
}

export function isTeamAiSkillImport(
  value: unknown,
): value is TeamAiSkillCreateInput {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const fields = Object.keys(record).sort();
  if (
    !(
      fields.length === SKILL_IMPORT_FIELDS.length &&
      fields.every((field, index) => field === SKILL_IMPORT_FIELDS[index]) &&
      typeof record.key === 'string' &&
      typeof record.name === 'string' &&
      typeof record.description === 'string' &&
      typeof record.content === 'string' &&
      (record.agent === 'pm' || record.agent === 'coder') &&
      (record.executable === null ||
        (typeof record.executable === 'object' &&
          !Array.isArray(record.executable))) &&
      typeof record.category === 'string' &&
      CATEGORY_OPTIONS.includes(record.category as TeamAiSkillCategory)
    )
  ) {
    return false;
  }
  const key = record.key as string;
  const name = (record.name as string).trim();
  const description = (record.description as string).trim();
  const content = (record.content as string).trim();
  return (
    key.length >= 3 &&
    key.length <= 100 &&
    /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(key) &&
    name.length >= 3 &&
    name.length <= 255 &&
    description.length >= 3 &&
    description.length <= 2_000 &&
    content.length >= 1 &&
    content.length <= MAX_SKILL_CONTENT_LENGTH
  );
}

export function describeSkillScope(
  scopeType: TeamAiSkillScope,
  scopeKey: string | null,
): string {
  return scopeType === 'platform'
    ? 'All Team AI V2 projects'
    : `${scopeType}: ${scopeKey || 'missing key'}`;
}
